import { FormAutosuggest, useZionFormContext } from '@fs/zion-form'
import React, { useCallback, useMemo, useState } from 'react'
import { Card, H6, Paragraph, Separator, TextField, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  getNestedOrFloatingElementById,
  getPersonRelationships,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { getRelationshipInputLabels } from './utils/eventUtils'
import ChooseRecordOverlay from './chooseRecordOverlay/ChooseRecordOverlay'

const RelationshipInputs = ({ eventLabel, person, personName, relationshipOptions }) => {
  const [t] = useTranslation()
  const { getValues, setValue, trigger, watch, formState: defaultValues } = useZionFormContext()
  const { images, currentImageArk, types } = useSlsStateContext()
  const chooseRecordOverlay = useOverlay()
  const [record1Relationships, setRecord1Relationships] = useState([])
  const [record2Relationships, setRecord2Relationships] = useState([])

  const relationshipPerson = watch('relationshipPerson')
  const [parentRelationship, eventType] = getValues(['parentRelationship', 'eventType'])
  const otherPersonId = useMemo(() => relationshipPerson?.key, [relationshipPerson])
  const { setDisplayHighlights } = useSlsHighlightsContext()
  const otherPerson = getNestedOrFloatingElementById(images, otherPersonId)
  const { mainPersonLabel, secondaryPersonLabel } = useMemo(
    () => getRelationshipInputLabels({ t, types, mainPerson: person, otherPerson, parentRelationship, eventType }),
    [t, types, otherPerson, parentRelationship, eventType, person]
  )

  const handleRelationshipPersonChange = useCallback(
    (value) => {
      // if moving a person to another record, we need to delete their relationships
      // User chooses which person has relationships deleted
      const newSelectedPerson = value ? getNestedOrFloatingElementById(images, value.key) : null
      if (newSelectedPerson) {
        const newSelectedPersonRecordId = newSelectedPerson.recordId

        if (newSelectedPersonRecordId && person.recordId !== newSelectedPersonRecordId) {
          const record1Rels = getPersonRelationships(person, images).filter((r) => r?.id !== parentRelationship?.id)
          const record2Rels = getPersonRelationships(newSelectedPerson, images)
          setRecord1Relationships(record1Rels)
          setRecord2Relationships(record2Rels)
          chooseRecordOverlay.open()
        }
      }

      // Basic form and highlights handling
      trigger('relationshipPerson')
      setDisplayHighlights({
        mainPartitionKey: currentImageArk,
        mainZoomFilter: (highlight) => highlight.attached,
        elementIds: [value?.key],
        fieldTypes: ['NAME_GN', 'NAME_SURN', 'NAME_SUF'],
      })
    },
    [trigger, currentImageArk, person, chooseRecordOverlay, parentRelationship?.id, images, setDisplayHighlights]
  )

  const setMovingToAnotherRecord = useCallback(
    (value) => {
      setValue('movingPersonToOtherRecord', value)
    },
    [setValue]
  )

  return (
    <>
      <Card edges="border">
        <H6>{t('edit-events.relationship.heading', 'Relationship')}</H6>
        <Paragraph size="sm">
          {t('edit-events.relationship.data', 'Select the couple in the {eventType}.', {
            eventType: eventLabel,
          })}
        </Paragraph>
        <Separator size="nano" />
        <TextField value={personName} label={mainPersonLabel} disabled />
        <FormAutosuggest
          name="relationshipPerson"
          label={secondaryPersonLabel}
          data-testid="relationship-person-autosuggest"
          placeholder={secondaryPersonLabel}
          suggestions={relationshipOptions}
          clearOnClickAway
          clearable
          onSuggestionSelected={handleRelationshipPersonChange}
        />
      </Card>
      <ChooseRecordOverlay
        onCancel={() => {
          setValue('relationshipPerson', defaultValues.relationshipPerson)
          trigger('relationshipPerson')
        }}
        person={person}
        otherPerson={otherPerson}
        overlay={chooseRecordOverlay}
        setMovingPersonToOtherRecord={setMovingToAnotherRecord}
        record1Relationships={record1Relationships}
        record2Relationships={record2Relationships}
      />
      <Separator size="xs" />
    </>
  )
}

export default RelationshipInputs
